import type { OccasionType, OrderDetailResponse, OrderResponse } from '@forbiz/contracts'

/**
 * 주문서 폼 상태와 payload 조립을 한곳에 모은다.
 *
 * 주문 생성 위저드(OrderCreatePage)와 상세 인라인 수정 바텀시트(OrderDetailPage)가
 * **같은 규칙으로** payload를 만들어야 한다 — 바텀시트에서 한 필드만 고쳐도 본문 전체를
 * 치환해 /body(amend)로 보내므로, 위저드가 만들었을 본문과 한 글자도 달라지면 안 된다.
 * 그래서 필드 목록·조립 로직을 여기 단일 원천으로 둔다.
 */

export interface FormState {
  deliveryAddress: string
  deliveryAddressDetail: string
  deliveryZipcode: string
  deliveryPlaceName: string
  desiredDate: string
  desiredTimeSlot: string
  deliveryNote: string
  recipientPhone: string
  senderName: string
  partnerName: string
  companyMemo: string
  quantity: string
  mortuaryRoom: string
  deceasedName: string
  mournerName: string
  eventType: string
  eventName: string
  eventTarget: string
  recipientName: string
  recipientOrg: string
  recipientDept: string
  recipientTitle: string
  ribbonLine1: string
  ribbonLine2: string
  cardMessage: string
}

export const EMPTY_FORM: FormState = {
  deliveryAddress: '', deliveryAddressDetail: '', deliveryZipcode: '', deliveryPlaceName: '',
  desiredDate: '', desiredTimeSlot: '', deliveryNote: '', recipientPhone: '', senderName: '',
  partnerName: '', companyMemo: '', quantity: '1',
  mortuaryRoom: '', deceasedName: '', mournerName: '',
  eventType: '', eventName: '', eventTarget: '',
  recipientName: '', recipientOrg: '', recipientDept: '', recipientTitle: '',
  ribbonLine1: '', ribbonLine2: '', cardMessage: '',
}

/** 선택된 유형에서 payload에 실을 필드 키. 교차 유형 필드를 애초에 안 담는다. */
export const FIELDS_BY_OCCASION: Record<OccasionType, string[]> = {
  FUNERAL: ['mortuaryRoom', 'deceasedName', 'mournerName', 'ribbonLine1', 'ribbonLine2'],
  CELEBRATION: ['eventType', 'eventName', 'eventTarget', 'ribbonLine1', 'ribbonLine2'],
  GIFT: ['recipientName', 'recipientOrg', 'recipientDept', 'recipientTitle', 'ribbonLine1', 'ribbonLine2'],
}

export const COMMON_FIELDS = [
  'deliveryAddress', 'deliveryAddressDetail', 'deliveryZipcode', 'deliveryPlaceName',
  'desiredDate', 'desiredTimeSlot', 'deliveryNote', 'recipientPhone', 'senderName', 'partnerName', 'companyMemo',
]

/** 기존 주문(상세·응답)을 폼 상태로 되돌린다 — 이어서 작성·amend·재주문 프리필에 공용. */
export function orderToForm(o: OrderDetailResponse | OrderResponse): FormState {
  return {
    deliveryAddress: o.deliveryAddress ?? '',
    deliveryAddressDetail: o.deliveryAddressDetail ?? '',
    deliveryZipcode: o.deliveryZipcode ?? '',
    deliveryPlaceName: o.deliveryPlaceName ?? '',
    desiredDate: o.desiredDate ?? '',
    desiredTimeSlot: o.desiredTimeSlot ?? '',
    deliveryNote: o.deliveryNote ?? '',
    recipientPhone: o.recipientPhone ?? '',
    senderName: o.senderName ?? '',
    partnerName: o.partnerName ?? '',
    companyMemo: o.companyMemo ?? '',
    quantity: String(o.item?.quantity ?? 1),
    mortuaryRoom: o.mortuaryRoom ?? '',
    deceasedName: o.deceasedName ?? '',
    mournerName: o.mournerName ?? '',
    eventType: o.eventType ?? '',
    eventName: o.eventName ?? '',
    eventTarget: o.eventTarget ?? '',
    recipientName: o.recipientName ?? '',
    recipientOrg: o.recipientOrg ?? '',
    recipientDept: o.recipientDept ?? '',
    recipientTitle: o.recipientTitle ?? '',
    ribbonLine1: o.ribbonLine1 ?? '',
    ribbonLine2: o.ribbonLine2 ?? '',
    cardMessage: o.cardMessage ?? '',
  }
}

/**
 * 폼 상태 → createOrderRequest payload(핵심부). 유형·상품·수량과 유형별 텍스트 필드만 담는다.
 * senderPresetId·partnerId 같은 선택 링크는 호출부(위저드)에서 덧붙인다 — 바텀시트는 순수 텍스트만 쓴다.
 * 빈 값은 담지 않는다(선택 필드 검증 회피). 리본 문구 2 = 보내는 이(발신자)로 합친다.
 */
export function buildOrderBody(
  occasion: OccasionType,
  productId: number,
  form: FormState,
  submit: boolean,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    occasionType: occasion,
    productId,
    quantity: Number(form.quantity) || 1,
    submit,
  }
  const keys = [...COMMON_FIELDS, ...FIELDS_BY_OCCASION[occasion]]
  for (const k of keys) {
    const v = form[k as keyof FormState].trim()
    if (v !== '') payload[k] = v // 빈 값은 안 담는다(날짜·선택 필드 검증 회피)
  }
  // 리본 문구 2 = 보내는 이(발신자). 발신자 입력을 senderName에 합쳤으므로 리본 데이터에도 실어준다.
  const senderVal = form.senderName.trim()
  if (senderVal) payload['ribbonLine2'] = senderVal
  return payload
}
